import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { architectureLayout } from '../architectureLayout';
import { useT } from '../i18n';
import './Architecture.css';

/**
 * An architecture diagram.
 *
 * The SVG draws the connectors and nothing else — every piece of text is HTML
 * sitting on top of it. That is deliberate: text needs the page's own type
 * rather than a font stack declared inside an SVG. It also means the SVG can
 * be hidden from assistive technology outright, with the nodes themselves
 * carrying the content.
 */

// Never below 0.85 — text stops being legible — and never above 1.4, where a
// small diagram starts looking cartoonish rather than merely filling its
// frame. Split out and exported so the clamp itself is unit-testable without
// a DOM.
export const clampScale = (value) => Math.min(1.4, Math.max(0.85, value));

// The six hues the design system allows, in the fixed order layers cycle
// through — colour here encodes a layer's role in the system, never
// decoration, so the list is deliberately closed. Records currently top out
// at four layers, but the modulo wraps rather than breaking on a fifth.
const LAYER_HUES = ['blue', 'green', 'amber', 'plum', 'teal', 'rose'];

// Exported so a test can pin the wrap-around behaviour without rendering.
export const layerHueVars = (layer) => {
  const index = ((layer % LAYER_HUES.length) + LAYER_HUES.length) % LAYER_HUES.length;
  const token = LAYER_HUES[index];
  return { '--layer-tone': `var(--c-${token})`, '--layer-tint': `var(--c-${token}-tint)` };
};

// The presentation-only scale factor that makes the diagram fill its frame:
// the larger of the two ratios would overflow one axis, so the smaller wins,
// then gets clamped to the legible/non-cartoonish range. Any zero dimension
// means measurement isn't available yet (or ever, as in jsdom) — 1 is the
// only sane fallback.
export const fitScale = (frameWidth, frameHeight, diagramWidth, diagramHeight) => {
  if (!frameWidth || !frameHeight || !diagramWidth || !diagramHeight) return 1;
  return clampScale(Math.min(frameWidth / diagramWidth, frameHeight / diagramHeight));
};

// The accessible text for the whole figure. Generated rather than authored so
// it can never drift from the picture. External nodes are marked inline —
// the dashed border is a purely visual convention otherwise, with no way for
// a screen-reader user to learn it applies to a given node.
//
// Module-level pure functions have no hook access, so `t` is threaded in as a
// parameter rather than called via useT() here — the caller (the component,
// or a test binding `translate` directly) owns which catalog resolves.
const nameOf = (node, t) => (node.kind === 'external' ? t('arch.external', { label: node.label }) : node.label);

export const describeArchitecture = (architecture, t) => {
  const labelOf = new Map();
  architecture.layers.forEach((layer) =>
    layer.nodes.forEach((node) => labelOf.set(node.id, nameOf(node, t)))
  );

  // Joiners ('. ', '; ', ', ') are punctuation, not translated text — they
  // hold the composed sentence together the same way in every language.
  const layers = architecture.layers
    .map((layer) =>
      t('arch.layer', { label: layer.label, nodes: layer.nodes.map((node) => nameOf(node, t)).join(', ') })
    )
    .join('. ');

  const edges = architecture.edges
    .map(([from, to, label]) => {
      const vars = { from: labelOf.get(from), to: labelOf.get(to), label };
      return label ? t('arch.edgeLabelled', vars) : t('arch.edge', vars);
    })
    .join('; ');

  return t('arch.caption', { layers, edges });
};

const Architecture = ({ architecture }) => {
  const t = useT();
  // useId produces ":r1:", which is a legal id but breaks url(#…) parsing.
  const markerId = `arch-arrow-${useId().replace(/:/g, '')}`;
  const { width, height, nodes, labels, edges } = architectureLayout(architecture);

  // Scales the diagram to fill its frame (see Projects.css's .arch-frame,
  // the scrolling ancestor this measures) instead of rendering at its raw
  // pixel size and leaving dead space around it. Geometry itself stays fixed
  // and untouched in architectureLayout — this is presentation only, and it
  // is the one place that's allowed to live outside that module.
  //
  // jsdom has neither ResizeObserver nor non-zero element rects, so both are
  // guarded: no observer means no measurement ever happens and scale simply
  // stays at its default of 1, which is exactly the right behaviour for
  // every test that renders this component without a sized ancestor.
  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const frame = stageRef.current?.closest('.arch-frame');
    if (!frame || typeof ResizeObserver === 'undefined') return undefined;

    const measure = () => {
      const rect = frame.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      setScale(fitScale(rect.width, rect.height, width, height));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [width, height]);

  const [hovered, setHovered] = useState(null);
  const [pinned, setPinned] = useState(null);

  // A pin outranks the pointer, so the highlight survives moving away from the
  // node that set it.
  const engaged = pinned ?? hovered;

  // Direct neighbours in both directions. One hop only: two hops lights most of
  // a small graph and stops meaning anything.
  const neighbours = useMemo(() => {
    if (!engaged) return null;
    const set = new Set([engaged]);
    edges.forEach((edge) => {
      if (edge.from === engaged) set.add(edge.to);
      if (edge.to === engaged) set.add(edge.from);
    });
    return set;
  }, [engaged, edges]);

  // Only attached while something is pinned, so at every other moment Escape
  // reaches the project modal's own document-level handler untouched and
  // closes the modal as usual.
  //
  // This diagram renders inside that modal (see Projects.js), whose Escape
  // handler is also a bubble-phase document listener with no stopPropagation
  // of its own — so if both fired, Escape would always close the whole modal
  // and a pinned node could never be released on its own. Registering here in
  // the capture phase (the trailing `true`) makes this listener run before
  // the modal's, and stopPropagation stops it from running at all. Do not
  // drop either half of that — losing the capture flag or the stopPropagation
  // silently regresses to "Escape always closes the modal".
  useEffect(() => {
    if (!pinned) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setPinned(null);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [pinned]);

  // null neighbours means nothing is engaged and everything renders plain.
  const state = (id) => {
    if (!neighbours) return '';
    if (id === engaged) return 'is-engaged';
    return neighbours.has(id) ? 'is-lit' : 'is-dimmed';
  };

  const edgeState = (edge) => {
    if (!neighbours) return '';
    return edge.from === engaged || edge.to === engaged ? 'is-lit' : 'is-dimmed';
  };

  return (
    <figure className="arch" style={{ width: width * scale }}>
      <div className="arch-scale-box" style={{ width: width * scale, height: height * scale }}>
        <div
          ref={stageRef}
          className="arch-stage"
          style={{ width, height, transform: `translate(-50%, -50%) scale(${scale})` }}
        >
          <svg
            className="arch-wires"
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
            focusable="false"
          >
            <defs>
              <marker
                id={markerId}
                markerWidth="7"
                markerHeight="7"
                refX="6"
                refY="3.5"
                orient="auto"
              >
                <path className="arch-head" d="M0,0 L7,3.5 L0,7 z" />
              </marker>
            </defs>
            {edges.map((edge, index) => (
              <path
                key={`${index}-${edge.from}-${edge.to}`}
                className={['arch-wire', edgeState(edge)].filter(Boolean).join(' ')}
                d={edge.d}
                style={{ '--i': index, ...layerHueVars(edge.sourceLayer) }}
                markerEnd={`url(#${markerId})`}
              />
            ))}

            {/* A second, purely decorative path per edge that carries the flow
                animation. Kept entirely separate from .arch-wire above rather
                than layered onto it: the entrance draw-in animates the same
                stroke-dashoffset property the flow would need, and a wire that
                becomes lit mid-entrance must not fight its own reveal. Static
                and invisible (opacity 0) until .is-lit; the wire it shadows is
                still the one thing screen readers and hit-testing ever see. */}
            {edges.map((edge, index) => (
              <path
                key={`${index}-${edge.from}-${edge.to}-flow`}
                className={['arch-flow', edgeState(edge)].filter(Boolean).join(' ')}
                d={edge.d}
                style={{ '--i': index, ...layerHueVars(edge.sourceLayer) }}
              />
            ))}
          </svg>

          {labels.map((label, index) => (
            <span
              key={`layer-${index}`}
              className="arch-layer"
              aria-hidden="true"
              // The box runs from the diagram's left edge to the end of the
              // gutter; text-align + the CSS translateY do the rest (see
              // Architecture.css) — right-aligned toward the row it labels,
              // vertically centred on that row's midpoint.
              style={{ left: 0, top: label.y, width: label.x, ...layerHueVars(index) }}
            >
              {label.text}
            </span>
          ))}

          {edges
            .filter((edge) => edge.label)
            .map((edge, index) => (
              <span
                key={`${index}-${edge.from}-${edge.to}-label`}
                className={['arch-wire-label', edgeState(edge)].filter(Boolean).join(' ')}
                aria-hidden="true"
                style={{ left: edge.labelX, top: edge.labelY }}
              >
                {edge.label}
              </span>
            ))}

          {nodes.map((node) => (
            <button
              key={node.id}
              type="button"
              data-node={node.id}
              className={[
                'arch-node',
                node.kind === 'external' ? 'is-external' : '',
                state(node.id),
              ].filter(Boolean).join(' ')}
              style={{
                left: node.x,
                top: node.y,
                width: node.w,
                height: node.h,
                '--i': node.layer,
                ...layerHueVars(node.layer),
              }}
              onMouseEnter={() => setHovered(node.id)}
              onMouseLeave={() => setHovered(null)}
              onFocus={() => setHovered(node.id)}
              onBlur={() => setHovered(null)}
              onClick={() => setPinned((current) => (current === node.id ? null : node.id))}
              aria-pressed={pinned === node.id}
              title={t('a11y.highlightConnections', { node: node.label })}
            >
              {node.label}
            </button>
          ))}
        </div>
      </div>

      {nodes.some((node) => node.kind === 'external') && (
        <p className="arch-legend">{t('arch.legend')}</p>
      )}

      <figcaption className="arch-caption">{describeArchitecture(architecture, t)}</figcaption>
    </figure>
  );
};

export default Architecture;
